import { SEVERITIES, type IncidentHistoryEntryResponse, type Severity } from '@prerender/diagnostics-api';
import type { ComponentType } from 'react';

import { ABSENT_VALUE } from './formatMeasuredValue';
import { isHistoryStatus } from './historyLabels';
import IncidentStatusTag from './IncidentStatusTag';
import SeverityTag from './SeverityTag';

export type TransitionEventType = 'severity_change' | 'status_change';

type TransitionEntry = Pick<IncidentHistoryEntryResponse, 'fromValue' | 'toValue'> & {
  eventType: TransitionEventType;
};

type ValueProps = { value: string };

const isSeverity = (value: string): value is Severity => (SEVERITIES as readonly string[]).includes(value);

function SeverityValue({ value }: ValueProps) {
  return isSeverity(value) ? <SeverityTag severity={value} /> : <>{value}</>;
}

function StatusValue({ value }: ValueProps) {
  return isHistoryStatus(value) ? <IncidentStatusTag status={value} /> : <>{value}</>;
}

const TRANSITION_KINDS: Record<TransitionEventType, { label: string; Value: ComponentType<ValueProps> }> = {
  severity_change: { label: 'Severity', Value: SeverityValue },
  status_change: { label: 'Status', Value: StatusValue },
};

export function isTransition<Entry extends Pick<IncidentHistoryEntryResponse, 'eventType'>>(
  entry: Entry
): entry is Entry & { eventType: TransitionEventType } {
  return Object.hasOwn(TRANSITION_KINDS, entry.eventType);
}

function HistoryTransition({ entry }: { entry: TransitionEntry }) {
  const { label, Value } = TRANSITION_KINDS[entry.eventType];

  return (
    <>
      {label} {entry.fromValue === null ? ABSENT_VALUE : <Value value={entry.fromValue} />}
      {' → '}
      {entry.toValue === null ? ABSENT_VALUE : <Value value={entry.toValue} />}
    </>
  );
}

export default HistoryTransition;
